import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Line,
  ComposedChart,
} from 'recharts';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import CardMedia from '@mui/material/CardMedia';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

// project imports
import MainCard from '../../../components/cards/MainCard';
import SkeletonEarningCard from '../../../components/cards/Skeleton/EarningCard';

// Service Imports
import { getWorkforceFlow } from '../../../services/EmployeeService';
import { getOffices } from '../../../services/officeAPIs';
import { getGoals } from '../../../services/goalAPIs';

// assets
import EmployeeIcon from '../../../assets/images/icons/emp.png';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import GetAppTwoToneIcon from '@mui/icons-material/GetAppOutlined';
import FileCopyTwoToneIcon from '@mui/icons-material/FileCopyOutlined';
import PictureAsPdfTwoToneIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ArchiveTwoToneIcon from '@mui/icons-material/ArchiveOutlined';
import FlagTwoToneIcon from '@mui/icons-material/FlagTwoTone';
import BusinessTwoToneIcon from '@mui/icons-material/BusinessTwoTone';

// Modern friendly colors
const modernColors = {
  all: '#FF9800', // Orange
  goalLine: '#2196F3', // Blue for goal line
};

const defaultOfficeGoals = {
  all: 0,
};

// Modern friendly colors
const ChartContainer = styled(Box)(({ theme }) => ({
  height: '200px',
  width: '100%',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  '& .recharts-cartesian-axis-tick-value': {
    fontSize: '11px',
    fill: theme.palette.text.primary,
  },
}));

const OfficeSelector = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5),
  background: 'linear-gradient(135deg, rgba(103, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
  borderRadius: '8px',
  border: '1px solid rgba(103, 126, 234, 0.2)',
  minWidth: '120px',
}));

const OfficeButton = styled(Button)(({ theme, selected, officecolor }) => ({
  fontSize: '0.75rem',
  padding: '8px 20px',
  minWidth: 'auto',
  textTransform: 'none',
  borderRadius: '20px',
  border: selected ? `2px solid ${officecolor}` : '1px solid rgba(103, 126, 234, 0.3)',
  background: selected
    ? 'linear-gradient(135deg, rgba(103, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)'
    : 'transparent',
  color: theme.palette.text.primary,
  fontWeight: selected ? 600 : 400,
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(103, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)',
    borderColor: officecolor,
    color: '#fff',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
  },
}));

const StatsChip = styled(Chip)(({ theme, officecolor }) => ({
  background: 'linear-gradient(135deg, rgba(103, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
  color: theme.palette.text.primary,
  fontSize: '0.7rem',
  height: '24px',
  fontWeight: 600,
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  border: `1px solid ${officecolor}`,
  borderRadius: '12px',
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(103, 126, 234, 0.25) 0%, rgba(118, 75, 162, 0.25) 100%)',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.2)',
  },
}));

const ClickableCard = styled(MainCard)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  background: 'linear-gradient(135deg, rgba(103, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
  },
}));

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    return (
      <Box
        sx={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 1) 100%)',
          padding: '8px 12px',
          borderRadius: '8px',
          border: '1px solid #e0e0e0',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: 'text.primary', fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}
        >
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Typography
            key={index}
            variant="body2"
            sx={{ color: entry.color, fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}
          >
            {entry.name === 'employees' ? 'Employees' : 'Goal'}: {entry.value}
          </Typography>
        ))}
        {data && typeof data.newJoins !== 'undefined' && data.newJoins !== null && (
          <>
            <Typography
              variant="body2"
              sx={{ color: '#4CAF50', fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}
            >
              New Joins: +{data.newJoins || 0}
            </Typography>
            {(data.departures > 0 || data.departures === 0) && (
              <Typography
                variant="body2"
                sx={{ color: '#F44336', fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}
              >
                Departures: -{data.departures || 0}
              </Typography>
            )}
            <Typography
              variant="body2"
              sx={{
                color: (data.netChange || 0) >= 0 ? '#4CAF50' : '#F44336',
                fontWeight: 600,
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
              }}
            >
              Net Change: {(data.netChange || 0) >= 0 ? '+' : ''}{data.netChange || 0}
            </Typography>
          </>
        )}
      </Box>
    );
  }
  return null;
};

// Export utilities
const exportToCSV = (data, filename) => {
  const headers = ['Month', 'Employees', 'Goal', 'New Joins', 'Departures', 'Net Change'];
  const csvContent = [
    headers.join(','),
    ...data.map(row => [
      row.month,
      row.employees || 0,
      row.goal || 0,
      row.newJoins || 0,
      row.departures || 0,
      row.netChange || 0
    ].join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

const copyToClipboard = async (data) => {
  const headers = ['Month', 'Employees', 'Goal', 'New Joins', 'Departures', 'Net Change'];
  const textContent = [
    headers.join('\t'),
    ...data.map(row => [
      row.month,
      row.employees || 0,
      row.goal || 0,
      row.newJoins || 0,
      row.departures || 0,
      row.netChange || 0
    ].join('\t'))
  ].join('\n');

  try {
    await navigator.clipboard.writeText(textContent);
    return true;
  } catch (err) {
    console.error('Failed to copy: ', err);
    return false;
  }
};

function EmployeeCard({ isLoading }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOffice, setSelectedOffice] = useState('all');
  const [employeeStats, setEmployeeStats] = useState({ total: 0 });
  const [chartData, setChartData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [offices, setOffices] = useState([]);
  const [officeColors, setOfficeColors] = useState({ ...modernColors });
  const [goals, setGoals] = useState([]);

  const currentYear = new Date().getFullYear();

  // Fetch goals
  useEffect(() => {
    const fetchGoals = async () => {
      try {
        const goalsData = await getGoals();
        setGoals(goalsData);
      } catch (error) {
        console.error('Error fetching goals:', error);
      }
    };
    fetchGoals();
  }, []);

  const generateOfficeColor = (officeName) => {
    let hash = 0;
    for (let i = 0; i < officeName.length; i++) {
      hash = officeName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 70%, 50%)`;
  };

  // Fetch offices
  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const officesData = await getOffices();
        setOffices(officesData);

        const colors = { ...modernColors };
        officesData.forEach((office) => {
          const key = office.name.toLowerCase().replace(/\s+/g, '');
          colors[key] = generateOfficeColor(key);
        });
        setOfficeColors(colors);
      } catch (error) {
        console.error('Error fetching offices:', error);
      }
    };
    fetchOffices();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setDataLoading(true);
      try {
        const officeParam = selectedOffice !== 'all'
          ? offices.find(o => o.name.toLowerCase().replace(/\s+/g, '') === selectedOffice)?.name
          : undefined;
        const workforce = await getWorkforceFlow(officeParam);

        const officeCounts = { total: workforce.total || 0 };
        if (workforce.byOffice) {
          Object.entries(workforce.byOffice).forEach(([key, value]) => {
            officeCounts[key.toLowerCase().replace(/\s+/g, '')] = value;
          });
        }
        setEmployeeStats(officeCounts);

        const currentGoalValue = getCurrentGoal();
        const monthlyWithGoal = (workforce.monthly || []).map((m) => ({
          ...m,
          goal: currentGoalValue,
        }));
        setChartData(monthlyWithGoal);
      } catch (error) {
        console.error('Error fetching workforce data:', error);
        setEmployeeStats({ total: 0 });
        setChartData([]);
      } finally {
        setDataLoading(false);
      }
    };

    if (offices.length > 0) {
      fetchData();
    }
  }, [selectedOffice, currentYear, offices.length, goals]);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleCardClick = (event) => {
    if (
      event.target.closest('[aria-controls="menu-earning-card"]') ||
      event.target.closest('.office-selector') ||
      event.target.closest('.goals-chips')
    ) {
      return;
    }
    navigate('/employees?tab=employee&office=' + selectedOffice);
  };

  const handleOfficeSelect = (office) => setSelectedOffice(office);

  const getCurrentEmployeeCount = () => {
    return selectedOffice === 'all' ? employeeStats.total || 0 : employeeStats[selectedOffice] || 0;
  };

  const getOfficeLabel = () => {
    if (selectedOffice === 'all') return 'All Offices';
    const office = offices.find(o => {
      const officeName = o.name.toLowerCase().replace(/\s+/g, '');
      return officeName === selectedOffice;
    });
    return office ? `${office.name} Office` : 'Office';
  };

  const getCurrentGoal = () => {
    if (selectedOffice === 'all') {
      return goals
        .filter(goal => goal.context === 'employees' && !goal.position && goal.location?.name)
        .reduce((sum, goal) => sum + (goal.scale || 0), 0);
    }

    const office = offices.find(o => {
      const officeName = o.name.toLowerCase().replace(/\s+/g, '');
      return officeName === selectedOffice;
    });

    if (!office) return 0;

    const officeGoal = goals.find(goal =>
      goal.context === 'employees' &&
      !goal.position &&
      goal.location?.name === office.name
    );

    return officeGoal ? (officeGoal.scale || 0) : 0;
  };

  const handleExportCSV = () => {
    const filename = `employee-data-${selectedOffice}-${new Date().getFullYear()}.csv`;
    exportToCSV(chartData, filename);
    handleClose();
  };

  const handleCopyData = async () => {
    const success = await copyToClipboard(chartData);
    handleClose();
  };

  return (
    <>
      {isLoading ? (
        <SkeletonEarningCard />
      ) : (
        <ClickableCard
          border={false}
          content={false}
          onClick={handleCardClick}
          sx={{
            bgcolor: 'background.paper',
            color: theme.palette.text.primary,
            overflow: 'hidden',
            position: 'relative',
            minHeight: '300px',
          }}
        >

          <Box sx={{ p: { xs: 1.5, sm: 2 }, position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  variant="rounded"
                  sx={{
                    ...theme.typography.commonAvatar,
                    ...theme.typography.mediumAvatar,
                    bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  <CardMedia
                    sx={{ width: 20, height: 20 }}
                    component="img"
                    src={EmployeeIcon}
                    alt="Employee"
                  />
                </Avatar>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 500, color: 'text.secondary' }}
                  >
                    Total Employees
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {getCurrentEmployeeCount()}
                    </Typography>
                    {dataLoading && <CircularProgress size={20} sx={{ color: 'primary.main' }} />}
                  </Box>
                </Box>
              </Box>
              <Avatar
                variant="rounded"
                sx={{
                  ...theme.typography.commonAvatar,
                  ...theme.typography.smallAvatar,
                  bgcolor: 'rgba(103, 126, 234, 0.1)',
                  color: theme.palette.text.primary,
                  zIndex: 1,
                }}
                aria-controls="menu-earning-card"
                aria-haspopup="true"
                onClick={handleClick}
              >
                <MoreHorizIcon fontSize="small" />
              </Avatar>
            </Box>

            {/* Chart + Office Selector Row */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', height: '100%' }}>
              {/* Left: Chart + Goal Chips */}
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <ChartContainer>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={chartData}
                      margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
                    >
                      <defs>
                        <linearGradient id={`colorEmployees-${selectedOffice}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={officeColors[selectedOffice] || modernColors.all} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={officeColors[selectedOffice] || modernColors.all} stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: theme.palette.text.primary }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                        domain={[0, 'auto']}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="goal"
                        stroke={modernColors.goalLine}
                        strokeWidth={2}
                        strokeDasharray="4 4"
                        dot={false}
                      />
                      <Area
                        type="monotone"
                        dataKey="employees"
                        stroke={officeColors[selectedOffice] || modernColors.all}
                        strokeWidth={2}
                        fillOpacity={1}
                        fill={`url(#colorEmployees-${selectedOffice})`}
                        dot={{
                          fill: officeColors[selectedOffice] || modernColors.all,
                          strokeWidth: 2,
                          stroke: theme.palette.background.paper,
                          r: 3,
                        }}
                        activeDot={{
                          r: 5,
                          stroke: officeColors[selectedOffice] || modernColors.all,
                          strokeWidth: 2,
                          fill: theme.palette.background.paper,
                        }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </ChartContainer>

                {/* ✅ Goal & Difference Chips - Inside chart container, below chart */}
                <Box
                  className="goals-chips"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 1,
                    mt: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <StatsChip
                    label={`Goal: ${getCurrentGoal()}`}
                    size="small"
                    officecolor={modernColors.goalLine}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (selectedOffice === 'all') {
                        navigate('/goals');
                      } else {
                        const formattedOffice = selectedOffice.charAt(0).toUpperCase() + selectedOffice.slice(1).toLowerCase();
                        navigate(`/goals?office=${formattedOffice}`);
                      }
                    }}
                  />
                  <StatsChip
                    label={`Difference: ${getCurrentEmployeeCount() - getCurrentGoal()}`}
                    size="small"
                    officecolor={
                      getCurrentGoal() - getCurrentEmployeeCount() > 0
                        ? 'red'
                        : getCurrentGoal() - getCurrentEmployeeCount() < 0
                          ? 'green'
                          : modernColors.goalLine
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (selectedOffice === 'all') {
                        navigate('/goals');
                      } else {
                        const formattedOffice = selectedOffice.charAt(0).toUpperCase() + selectedOffice.slice(1).toLowerCase();
                        navigate(`/goals?office=${formattedOffice}`);
                      }
                    }}
                  />
                </Box>
              </Box>

              {/* Right: Office Selector */}
              <Box sx={{ flex: '0 0 auto', minWidth: '180px', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography
                    sx={{
                      fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      fontWeight: 600,
                      color: 'text.primary',
                      mb: 0.5,
                    }}
                  >
                    {getCurrentEmployeeCount()}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      fontWeight: 500,
                      color: 'text.secondary',
                    }}
                  >
                    {getOfficeLabel()}
                  </Typography>
                </Box>

                <Box className="office-selector">
                  <OfficeSelector>
                    <OfficeButton
                      size="small"
                      selected={selectedOffice === 'all'}
                      officecolor={officeColors.all}
                      onClick={() => handleOfficeSelect('all')}
                    >
                      All
                    </OfficeButton>
                    {offices.map((office) => {
                      const officeName = office.name.toLowerCase().replace(/\s+/g, '');
                      return (
                        <OfficeButton
                          key={office._id}
                          size="small"
                          selected={selectedOffice === officeName}
                          officecolor={officeColors[officeName] || modernColors.all}
                          onClick={() => handleOfficeSelect(officeName)}
                        >
                          {office.name}
                        </OfficeButton>
                      );
                    })}
                  </OfficeSelector>
                </Box>
              </Box>
            </Box>

            {/* Menu */}
            <Menu
              id="menu-earning-card"
              className="menu-earning-card"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
              variant="selectedMenu"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              sx={{
                '& .MuiPaper-root': {
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 1) 100%)',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              <MenuItem onClick={(e) => { e.stopPropagation(); navigate('/goals'); }}>
                <FlagTwoToneIcon sx={{ mr: 1.75 }} /> New Goal
              </MenuItem>
              <MenuItem onClick={(e) => { e.stopPropagation(); navigate('/offices'); }}>
                <BusinessTwoToneIcon sx={{ mr: 1.75 }} /> New Office
              </MenuItem>
              <MenuItem onClick={handleExportCSV}>
                <GetAppTwoToneIcon sx={{ mr: 1.75 }} /> Export CSV
              </MenuItem>
              <MenuItem onClick={handleCopyData}>
                <FileCopyTwoToneIcon sx={{ mr: 1.75 }} /> Copy Data
              </MenuItem>
              <MenuItem onClick={handleClose}>
                <PictureAsPdfTwoToneIcon sx={{ mr: 1.75 }} /> Export PDF
              </MenuItem>
              <MenuItem onClick={handleClose}>
                <ArchiveTwoToneIcon sx={{ mr: 1.75 }} /> Archive File
              </MenuItem>
            </Menu>
          </Box>
        </ClickableCard>
      )}
    </>
  );
}

EmployeeCard.propTypes = {
  isLoading: PropTypes.bool,
};

export default React.memo(EmployeeCard);